import React, { useContext, useEffect, useState } from 'react';
import { WelletContext } from '../../../context/WelletContext';
import { CircleSpinner } from 'react-spinners-kit';
import { useSelector } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { convertTime12to24, getDatePerformance, getLocale } from '../../../helpers/dates';
import { useTranslation } from 'react-i18next';
import useForm from '../../../helpers/useForm';
import ContentApp from '../../../components/ContentApp';
import ButtonProccessing from '../../../components/Common/ButtonProccessing/ButtonProccessing';
import OccasionSelector from '../../../components/ReservationCheck/OccasionSelector/OccasionSelector';
import ReservationCheckCustomerInfo from '../../../components/ReservationCheck/ReservationCheckCustomerInfo/ReservationCheckCustomerInfo';
import CustomerSpecialRequest from '../../../components/ReservationCheck/CustomerSpecialRequest/CustomerSpecialRequest';
import Header from '../../../components/Common/Header/Header';
import ErrorBox from '../../../components/Common/ErrorBox/ErrorBox';
import styles from './reservationCheck.module.css';
import { getQueryString } from '../../../helpers/url';
import '../../../components/ReservationDetail/reservation-details.css';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import ContentBottom from '../../../components/ContentBottom';
import PaxSelector from '../../../components/ReservationSelector/PaxSelector';
import PerformanceSelector from '../../../components/PerformanceSelector/PerformanceSelector';
import { format } from 'date-fns';

const ReservationNoGuaranteed = ({ history }) => {
  const ButtonStatus = {
    Normal: 'NORMAL',
    InProgress: 'INPROGRESS',
    ProgressFinished: 'FINISHED',
    Error: 'ERROR',
  };
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const showId = getQueryString('showId');
  const eventId = getQueryString('eventId');
  const countryCode = useSelector((state) => state.app.currentMarket.countryCode);
  const reservation = useSelector((state) => state.reservation);
  const hasProducts = reservation.products?.length > 0;

  const language = useSelector((state) => state.app.language);
  const [selectedButton, setSelectedButton] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [btnStatus, setBtnStatus] = useState(ButtonStatus.Normal);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [initialNoAvailabilityError, setInitialNoAvailabilityError] = useState(null);

  const [eventDetail, setEventDetail] = useState(null);
  const [reservationInfo, setReservationInfo] = useState(null);

  //const performanceObj = performance && parse(performance, 'HH:mm', new Date());
  //const shouldAddDay = performanceObj && performanceObj.getHours() >= 0 && performanceObj.getHours() <= 3;

  const [intervals, setIntervals] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [selectedPax, setSelectedPax] = useState(reservation.paxs);
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const idReservation = getQueryString('id');

  const handleSelectOccasion = (value) => {
    if (selectedButton === value) {
      setSelectedButton(null);
      handleChange(undefined, 'occasion', null);
    } else {
      handleChange(undefined, 'occasion', value);
      setSelectedButton(value);
    }
  };

  const getEventDetails = async () => {
    try {
      const response = await welletContext.apis.tickets.get(
        `/shows/event/${eventId}?lang=${language}`,
      );
      setEventDetail(response.data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  function validate(values) {
    let errors = {};
    validateCustomerInfo(values, errors);

    if (eventDetail.includeAvailability && !selectedPerformance) {
      errors.performance = t('selectArrivalTime');
    }

    return errors;
  }

  function validateCustomerInfo(values, errors) {
    if (!values.customerName) {
      errors.customerName = t('nameError.EMPTY_NAME');
    } else if (/\d/.test(values.customerName)) {
      errors.customerName = t('nameError.NOT_NUMBERS');
    }

    if (!values.customerLastname) {
      errors.customerLastname = t('nameError.EMPTY_LASTNAME');
    } else if (/\d/.test(values.customerLastname)) {
      errors.customerLastname = t('nameError.NOT_NUMBERS');
    }

    if (!values.phone) {
      errors.phone = t('phoneError.EMPTY_PHONE_NUMBER');
      setPhoneError(errors.phone);
    } else {
      setPhoneError('');
    }

    if (values.phone && !isValidPhoneNumber(values.phone)) {
      errors.phone = t('phoneError.INVALID_PHONE_NUMBER');
      setPhoneError(errors.phone);
    }

    if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    return errors;
  }

  const sendForm = async () => {
    setBtnStatus(ButtonStatus.InProgress);
    const reservationLocalTime = eventDetail.includeAvailability
      ? getDatePerformance(reservation.date, selectedPerformance)
      : eventDetail.startDate;

    try {
      let url = '/reservation/GenerateReservation';
      if (reservation && idReservation) {
        url = '/reservation/modifyreservation';
      }

      const result = await welletContext.apis.tickets.put(url, {
        showId: reservation.showId,
        paxs: selectedPax,
        customer: {
          firstName: values.customerName,
          lastName: values.customerLastname,
          phone: values.phone,
          email: values.email,
        },
        email: values.email ?? '',
        comments: values.comments ?? '',
        occasion: selectedButton ?? null,
        countryCode: values.countryCode ?? 'MX',
        reservationDateLocalTime: reservationLocalTime,
        totalAmount: 0,
        products: reservation.products ?? null,
        oldReservationId: idReservation,
        eventName: null,
        availabilityRefId: null,
        eventId: eventId,
        language: null, //It shouldn't be sent, but the API sets it to "ES".
      });

      setBtnStatus(ButtonStatus.ProgressFinished);
      setTimeout(() => {
        history.push(
          `/reservation-success?reservationType=${result.data.reservationType}&status=${result.data.status}`,
        );
      }, 1500);
    } catch (error) {
      setBtnStatus(ButtonStatus.Error);
      let errorMessage;
      switch (error.response.data.errorType) {
        case 'GENERIC_ERROR':
          errorMessage = t('reservationError.GENERIC_ERROR');
          break;
        case 'SHOW_NOT_FOUND':
          errorMessage = t('reservationError.SHOW_NOT_FOUND');
          break;
        case 'SCHEDULE_SOLD_OUT':
          errorMessage = t('reservationError.SCHEDULE_SOLD_OUT');
          break;
        case 'INVALID_RESERVATION_DATE':
          errorMessage = t('reservationError.INVALID_RESERVATION_DATE');
          break;
        case 'NO_AVAILABILITY':
          errorMessage = t('reservationError.NO_AVAILABILITY');
          break;
        case 'DUPLICATE_RESERVATION':
          errorMessage = t('reservationError.DUPLICATE_RESERVATION');
          break;
        default:
          errorMessage = t('reservationError.GENERIC_ERROR');
      }
      setError(errorMessage);
    }
  };

  let { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);
  //const alignedDate = date && alignTimezone(date);
  //const adjustedDate = shouldAddDay ? addDays(alignedDate, 1) : alignedDate;

  const getSchedule = async () => {
    setLoadingSchedule(true);

    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/GetScheduleEvent?eventId=${eventId}&reservationType=AREA`,
      );

      if (result.data.schedules.length === 0) {
        setIntervals([]);
      }

      const formattedTimes = result.data.schedules.map((time) => {
        const time24 = new Date(reservation.date + 'T' + convertTime12to24(time.timeStart));

        if (time.timeMinutesStart >= 0 && time.timeMinutesStart <= 360)
          time24.setDate(time24.getDate() + 1);

        return { time24, soldOut: time.isSoldOut };
      });
      formattedTimes.sort();

      const allIntervals = formattedTimes.map(({ time24, soldOut }) => ({
        time: time24,
        soldOut,
      }));

      setIntervals(allIntervals);
      setLoadingSchedule(false);
    } catch (error) {
      console.error(error);
    }
  };

  const setInitialValues = (reservation) => {
    handleChange(null, 'customerName', reservation.customerFirstName);
    handleChange(null, 'customerLastname', reservation.customerLastName);
    handleChange(null, 'phone', reservation.customerPhone);
  };

  const getReservation = async () => {
    setLoading(true);
    try {
      const response = await welletContext.apis.tickets.get(
        `/Reservation/${idReservation}?lang=${language}`,
      );
      setReservationInfo(response.data);
      setInitialValues(response.data);
      setSelectedPax(response.data.paxs);
      setSelectedPerformance(format(new Date(response.data.reservationLocalDate), 'H:mm'));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventDetails();
    getSchedule(reservation.date);
    handleChange(null, 'language', language);

    if (idReservation) getReservation();
  }, []);

  return loading ? (
    <div className='loaderDiv'>
      <CircleSpinner
        className='spinner-app mx-auto'
        style={{ margin: 'auto' }}
        size={40}
        color='var(--color-font-primary)'
      />
    </div>
  ) : (
    <>
      <Header />
      <ContentApp classes='pb-0'>
        <div className={`order-details ${styles.reservation}`}>
          {initialNoAvailabilityError ? (
            <>
              <ErrorBox title={initialNoAvailabilityError} />
              <div className='mt-4 text-center text-primary' onClick={() => history.goBack()}>
                {t('reschedule')}
              </div>
            </>
          ) : (
            <>
              <EventHeader
                eventImg={eventDetail.bannerImage}
                eventName={eventDetail.name}
                eventDescription={eventDetail.show.name}
                eventDate={eventDetail.startDate}
              />

              <div className='mt-4'>
                <div className='fw-semibold text-primary font-tiny mb-3'>{t('numberGuests')}</div>
                <PaxSelector
                  defaultPax={selectedPax}
                  handleChangePax={(value) => setSelectedPax(value)}
                />
              </div>

              {reservationInfo?.customerName ? (
                <div className='mt-4'>
                  <span className='fw-semibold text-primary font-tiny'>
                    {t('reservationInformation')}
                  </span>
                  <div className='mt-2 text-secondary text-small'>{t('bookingHolder')}</div>
                  <span className='font-medium fw-semibold mt-1 '>
                    {reservationInfo?.customerName}
                  </span>
                  {reservationInfo?.occasion && (
                    <>
                      <div className='mt-3 text-secondary text-small'>{t('bookingHolder')}</div>
                      <span className='text-tertiary fw-semibold mt-1'>
                        {reservationInfo?.occasion}
                      </span>
                    </>
                  )}
                  {reservationInfo?.comments && (
                    <>
                      <div className='mt-3 text-secondary text-small'>{t('specialRequest')}</div>
                      <span className='text-tertiary fw-semibold mt-1'>
                        {reservationInfo?.comments}
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className='mt-4'>
                    <div className='fw-semibold text-primary font-tiny mb-3'>
                      {t('reservationInformation')}
                    </div>
                    <ReservationCheckCustomerInfo
                      handleChange={handleChange}
                      values={values}
                      errors={errors}
                      phoneError={phoneError}
                      setPhoneError={setPhoneError}
                      countryCode={countryCode}
                    />
                  </div>

                  {/* <div className='mt-4'>
                    <OccasionSelector
                      handleSelectButton={handleSelectOccasion}
                      selectedButton={selectedButton}
                    />
                  </div> */}

                  <div className='mt-4'>
                    <CustomerSpecialRequest
                      open={open}
                      setOpen={setOpen}
                      values={values}
                      handleChange={handleChange}
                    />
                  </div>
                </>
              )}
              {error && (
                <div className='mt-4'>
                  <>
                    <ErrorBox title={error} />
                    <div className='mt-4 text-center text-primary' onClick={() => history.goBack()}>
                      {t('reschedule')}
                    </div>
                  </>
                </div>
              )}
              {eventDetail?.includeAvailability &&
                (intervals.length === 0 && hasProducts ? (
                  <div className='mt-3'>
                    <EmptyActivity title={t('noSchedule')} />
                  </div>
                ) : (
                  <>
                    <div className='position-relative mt-4'>
                      <div className='fw-semibold text-primary font-tiny'>{t('arrivalTime')}</div>
                      <div className='mt-3'>
                        <PerformanceSelector
                          performances={intervals}
                          onHandleChange={setSelectedPerformance}
                          defaultPerformance={selectedPerformance}
                        />
                      </div>
                      {errors.performance && errors.performance.length > 0 && (
                        <div
                          className='errorLabel mt-2 position-relative error-label'
                          style={{ color: '#fff' }}
                        >
                          <span></span> {errors.performance}
                        </div>
                      )}
                    </div>
                  </>
                ))}

              <div className='mt-4'>
                <div className='fw-semibold text-primary font-tiny'>{t('termsAndConditions')}</div>
                <div className='text-tertiary font-small mt-2'>{t('noGuaranteedDescription')}</div>
              </div>
            </>
          )}
        </div>
      </ContentApp>
      <ContentBottom>
        <div className='mt-4'>
          <ButtonProccessing
            status={btnStatus}
            text={t('requestReservation')}
            size='large'
            className={styles.btn}
            type='button'
            onClick={handleSubmit}
            arrow
            color='#000'
            backgroundColor='var(--color-primary)'
            iconColor='#000'
          />
        </div>
      </ContentBottom>
    </>
  );
};

export default ReservationNoGuaranteed;
